import { Currency } from "@redotech/money/currencies";
import { checkoutOptimizationBillingSchema } from "@redotech/redo-billing-common/checkout-optimization-billing-schema";
import { InsertionPosition } from "@redotech/redo-storefront-common/insertion-position";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { FulfillmentCutoffScheduleParser } from "./fulfillment-cutoff";

/**
 * Merchant-app Conversion Cloud surfaces that can be independently locked via
 * admin-managed `checkoutOptimization.lockedFeatures`. These are **frontend
 * presentation hooks only** (sidebar hide/lock-icon + merchant-app route
 * redirects) — they do not disable storefront serving, billing, or live
 * widgets. Dashboard and Welcome are intentionally not members.
 */
export enum ConversionFeature {
  AB_TESTS = "AB_TESTS",
  ANALYTICS_CHECKOUT = "ANALYTICS_CHECKOUT",
  ANALYTICS_POST_PURCHASE = "ANALYTICS_POST_PURCHASE",
  ANALYTICS_CUSTOM_REPORTS = "ANALYTICS_CUSTOM_REPORTS",
  CHECKOUTS = "CHECKOUTS",
  DISCOUNT_RULES = "DISCOUNT_RULES",
  POST_PURCHASE = "POST_PURCHASE",
  PRICING_TESTS = "PRICING_TESTS",
  SPOTLIGHTS = "SPOTLIGHTS",
  WIDGETS = "WIDGETS",
}

export const CONVERSION_FEATURES = Object.values(
  ConversionFeature,
) as ConversionFeature[];

/**
 * Canonical PPU / self-serve intent lock list: every surface except Spotlights.
 * Written by activateIntentRescue and by manual migration of intent-only teams.
 */
export const DEFAULT_INTENT_LOCKED_FEATURES: readonly ConversionFeature[] =
  CONVERSION_FEATURES.filter((f) => f !== ConversionFeature.SPOTLIGHTS);

// Analytics locks vary per team, so they never affect intent-only cohort
// membership (ignored whether locked or not).
const INTENT_ONLY_COHORT_IGNORED_FEATURES: readonly ConversionFeature[] = [
  ConversionFeature.ANALYTICS_CHECKOUT,
  ConversionFeature.ANALYTICS_POST_PURCHASE,
  ConversionFeature.ANALYTICS_CUSTOM_REPORTS,
];

// Defines "intent-only" admin dashboard cohort membership
// (getIntentSelfServeTrials); the spec pins it so enum growth forces a decision.
export const INTENT_ONLY_COHORT_REQUIRED_LOCKS: readonly ConversionFeature[] =
  CONVERSION_FEATURES.filter(
    (f) =>
      f !== ConversionFeature.SPOTLIGHTS &&
      !INTENT_ONLY_COHORT_IGNORED_FEATURES.includes(f),
  );

/**
 * PPU-intent self-serve lock list: unlocks Spotlights, Post-purchase, checkout
 * analytics, and post-purchase analytics.
 */
export const DEFAULT_PPU_INTENT_SELF_SERVE_LOCKED_FEATURES: readonly ConversionFeature[] =
  CONVERSION_FEATURES.filter(
    (f) =>
      f !== ConversionFeature.SPOTLIGHTS &&
      f !== ConversionFeature.POST_PURCHASE &&
      f !== ConversionFeature.ANALYTICS_CHECKOUT &&
      f !== ConversionFeature.ANALYTICS_POST_PURCHASE,
  );

/**
 * PPU-intent admin dashboard cohort (getPpuIntentSelfServeMerchants); pinned by
 * spec. POST_PURCHASE unlocked is the discriminator; analytics locks ignored.
 */
export const PPU_INTENT_COHORT_REQUIRED_LOCKS: readonly ConversionFeature[] =
  CONVERSION_FEATURES.filter(
    (f) =>
      f !== ConversionFeature.SPOTLIGHTS &&
      f !== ConversionFeature.POST_PURCHASE &&
      !INTENT_ONLY_COHORT_IGNORED_FEATURES.includes(f),
  );

/**
 * Whether `lockedFeatures` matches the PPU-intent cohort: all required locks
 * present, Spotlights and Post-purchase unlocked (analytics ignored).
 */
export function matchesPpuIntentLockConfig(
  lockedFeatures: readonly ConversionFeature[],
): boolean {
  return (
    !lockedFeatures.includes(ConversionFeature.SPOTLIGHTS) &&
    !lockedFeatures.includes(ConversionFeature.POST_PURCHASE) &&
    PPU_INTENT_COHORT_REQUIRED_LOCKS.every((feature) =>
      lockedFeatures.includes(feature),
    )
  );
}

const postPurchaseUpsellSchema = z.object({
  createOrderOnUpsell: z.boolean().optional(),
});

const progressBarSchema_ = z.object({
  threshold: z.object({
    amount: z.number(),
    currency: createEnum(Currency, { enumName: "Currency" }),
  }),
  ltThresholdText: z.string(),
  gteThresholdText: z.string(),
  progressBarColor: z.string(),
  selectors: z.array(
    z.object({
      selector: z.string(),
      insertPosition: createEnum(InsertionPosition, {
        enumName: "InsertionPosition",
      }),
    }),
  ),
});
export const progressBarSchema: z.ZodType<ProgressBar> = progressBarSchema_;
export interface ProgressBar extends z.infer<typeof progressBarSchema_> {}

const checkoutOptimizationSettingsSchema = z.object({
  enabled: z.boolean(),
  infinityTestingEnabled: z.boolean().optional(),
  preventSplitShipmentsRateAddition: z.boolean().optional(),
  filterFedexOneRateRates: z.boolean().optional(),
  postPurchaseUpsell: postPurchaseUpsellSchema.optional(),
  fulfillmentCutoffSchedule: FulfillmentCutoffScheduleParser.optional(),
  checkoutOptimizationBilling: checkoutOptimizationBillingSchema.optional(),
  ensureDiscountsCombineWithShippingDiscounts: z.boolean().optional(),
  subscriptionsModeEnabled: z.boolean().optional(),
  /**
   * When enabled (and subscriptions mode is on), the rates endpoint skips the
   * invisible rate-name uniqueness character for the first occurrence of each
   * rate name, so rate names stay clean for consumers that bypass the delivery
   * customization function. See `stripCheckoutOptimizationInvisibleIndicators`.
   */
  hideInvisibleRateNameCharsWhenPossible: z.boolean().optional(),
  storefrontProgressBar: progressBarSchema_.optional(),
  useShippingOptionMetafields: z.boolean().optional(),
  isUsingDiscountRules: z.boolean().optional(),
  syncDeliveryProfileProductMetafields: z.boolean().optional(),
  /**
   * Frontend presentation hooks for the merchant Conversion app (sidebar +
   * in-app routes only — not storefront). Empty = nothing locked. Populated
   * for partial tiers / after intent-only merchants migrate to
   * checkoutOptimization.enabled + a lock list.
   */
  lockedFeatures: z
    .array(createEnum(ConversionFeature, { enumName: "ConversionFeature" }))
    .optional()
    .default([]),
});

export interface CheckoutOptimizationSettings extends z.infer<
  typeof checkoutOptimizationSettingsSchema
> {}

export const checkoutOptimizationSettingsDefault: CheckoutOptimizationSettings =
  {
    enabled: false,
    infinityTestingEnabled: false,
    preventSplitShipmentsRateAddition: false,
    filterFedexOneRateRates: false,
    ensureDiscountsCombineWithShippingDiscounts: false,
    lockedFeatures: [],
  };

const CheckoutOptimizationAnalyticsAssumptionsZodSchema = z.object({
  annualOrders: z.number().optional(),
});
export interface CheckoutOptimizationAnalyticsAssumptions extends z.infer<
  typeof CheckoutOptimizationAnalyticsAssumptionsZodSchema
> {}

export interface CheckoutOptimizationBillingCreator {
  /**
   * Distinguishes this billing creator type from any other string to
   * ensure the creator conforms to the correct string format.
   */
  __checkoutOptimizationBillingCreator: true;
  value: string;
}

export function getBillingSettingsCreator({
  creatorEmail,
}: {
  creatorEmail: string;
}): CheckoutOptimizationBillingCreator {
  return {
    __checkoutOptimizationBillingCreator: true,
    value: `admin:${creatorEmail}`,
  };
}

export function getSystemBillingSettingsCreator(
  label: string,
): CheckoutOptimizationBillingCreator {
  return {
    __checkoutOptimizationBillingCreator: true,
    value: `system:${label}`,
  };
}
